import { normalizeVector } from './matcher';

type WorkerResponse =
  | { type: 'ready'; backend: string }
  | { type: 'progress'; message: string; percent?: number }
  | { type: 'embedding'; requestId: number; embedding: ArrayBuffer }
  | { type: 'error'; requestId?: number; message: string };

interface PendingRequest {
  resolve: (embedding: Float32Array) => void;
  reject: (error: Error) => void;
}

export class ModelClient {
  private readonly worker = new Worker(new URL('../model.worker.ts', import.meta.url), {
    type: 'module',
  });
  private requestId = 0;
  private pending = new Map<number, PendingRequest>();
  private readyPromise: Promise<string>;
  private progressHandler: (message: string, percent?: number) => void = () => undefined;

  constructor() {
    this.readyPromise = new Promise((resolve, reject) => {
      const onInitialMessage = (event: MessageEvent<WorkerResponse>) => {
        const message = event.data;
        if (message.type === 'ready') {
          resolve(message.backend);
        } else if (message.type === 'error' && message.requestId === undefined) {
          reject(new Error(message.message));
        }
      };
      this.worker.addEventListener('message', onInitialMessage);
    });

    this.worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
      const message = event.data;
      if (message.type === 'progress') {
        this.progressHandler(message.message, message.percent);
        return;
      }
      if (message.type === 'embedding') {
        const pending = this.pending.get(message.requestId);
        if (!pending) return;
        this.pending.delete(message.requestId);
        pending.resolve(normalizeVector(new Float32Array(message.embedding)));
        return;
      }
      if (message.type === 'error' && message.requestId !== undefined) {
        const pending = this.pending.get(message.requestId);
        if (!pending) return;
        this.pending.delete(message.requestId);
        pending.reject(new Error(message.message));
      }
    });
  }

  onProgress(handler: (message: string, percent?: number) => void): void {
    this.progressHandler = handler;
  }

  async ready(): Promise<string> {
    return this.readyPromise;
  }

  async embed(imageData: ImageData): Promise<Float32Array> {
    await this.readyPromise;
    const requestId = ++this.requestId;
    const buffer = imageData.data.buffer.slice(0);

    return new Promise((resolve, reject) => {
      this.pending.set(requestId, { resolve, reject });
      this.worker.postMessage(
        {
          type: 'embed',
          requestId,
          width: imageData.width,
          height: imageData.height,
          pixels: buffer,
        },
        [buffer],
      );
    });
  }

  dispose(): void {
    this.worker.terminate();
    for (const pending of this.pending.values()) {
      pending.reject(new Error('模型工作线程已关闭。'));
    }
    this.pending.clear();
  }
}
